/**
 * WikipediaEnricher — fetches Wikipedia REST API summaries + search.
 *
 * Image quality filter: not all Wikipedia images are suitable for the
 * audio guide UI. Maps, coats of arms, diagrams, and SVGs are filtered
 * out — the app already has its own interactive map with glowing pins.
 * Only photos, portraits, building shots, and artwork are shown.
 */
/**
 * Patterns that indicate a map, diagram, coat of arms, or other non-photo.
 * Matched against the FULL image URL (not just filename) because Wikipedia
 * thumbnail URLs embed the original filename in the path.
 * Uses [_\-/. ] as delimiters instead of \b because underscore is a word
 * character in regex, causing \bkarte\b to miss "Karte_A_Ktn_K".
 */
const BAD_IMAGE_PATTERNS = /(map|karte|lage|location|locator|position|relief|topograph|administ|distri[ck]t|provinces?|boundar|subdiv|wappen|coat.of.arms|blazon|escudo|herb_|flag_of|flagge|banner|diagramm?|chart|graph|schema|grundriss|floor.plan|timeline|pictogram|seismic|earthquake|erdbeben|hazard|intensity|magnitude|bezirk|gemeinde|kreis|county|region|oblast|provincia|cantone|kommune|municipalit|geo_|_geo|profil|querschnitt|cross.section|geolog)[_\-/.]/i;
/** SVG in any part of the URL path (Wikipedia renders SVGs as PNG thumbnails) */
const BAD_SVG = /\.svg[/.]/i;
/** Very small images (Wikipedia icons, pictograms) */
const MIN_USEFUL_DIMENSION = 150;
/**
 * Detect geographic entities by their Wikipedia description.
 * Works across all languages because Wikipedia standardizes these.
 * Geographic entities almost always show a map as main image.
 */
function _isGeographicDescription(desc) {
    return /\b(gemeinde|stadt|city|town|village|municipality|commune|comune|ciudad|ville|dorf|ort|markt|bezirk|kreis|county|district|region|provincia|département|oblast|cantone|prefecture|capital|hauptstadt|landeshauptstadt|bundesland|state|province|country|island|insel|isola|île|gebirgsgruppe|gebirgszug|höhenzug|bergkette|mountain.range|mountain.group|ridge|massif|massiv|tal|valley|vallée|schlucht|gorge|canyon|plateau|hochebene|ebene|plain|becken|basin)\b/i.test(desc);
}
/** ISO-3166 alpha-2 → Sprache der LOKALEN Wikipedia. Konservative Allowlist:
 *  nur Sprachen, deren Wikis lokale Sehenswürdigkeiten zuverlässig führen.
 *  Im Ausland findet die GEO-Suche der lokalen Wiki Denkmäler (mit Foto), auf
 *  die ein deutsch/englisch geratener Titel 404t — z.B. Ružica grad (hr). */
const WIKI_LANG_BY_CC = {
    at: 'de', de: 'de', ch: 'de',
    hr: 'hr', si: 'sl', it: 'it', fr: 'fr', es: 'es', pt: 'pt', nl: 'nl',
    cz: 'cs', sk: 'sk', hu: 'hu', pl: 'pl', ba: 'bs', rs: 'sr', gr: 'el', tr: 'tr',
    gb: 'en', ie: 'en', us: 'en',
};
/** Lokale Wiki-Sprache für einen lowercase-ISO-Ländercode (null = unbekannt). */
export function wikiLangForCountry(cc) {
    return (cc && WIKI_LANG_BY_CC[cc]) || null;
}
export class WikipediaEnricher {
    async enrich(title, lang = 'de', userLat, userLon, city, localLang, guessLat, guessLon) {
        // 1. Direct title lookup — but verify geo-proximity if we have user location.
        // "Como" (café in Klagenfurt) matches "Como" (city in Italy) exactly.
        let result = await this._fetchSummary(title, lang);
        if (result) {
            if (userLat !== undefined && userLon !== undefined) {
                const coords = await this._fetchCoordinates(title, lang);
                if (coords) {
                    const dist = WikipediaEnricher._haversineKm(userLat, userLon, coords.lat, coords.lon);
                    if (dist > 50) {
                        // Direct match is far away — likely a different place with same name.
                        // Skip this result entirely and try geosearch fallback.
                        result = null;
                    }
                    else {
                        result.lat = coords.lat; // verified coordinate → drives the marker
                        result.lon = coords.lon;
                    }
                }
            }
            if (result)
                return result;
        }
        // 2. Try English Wikipedia
        if (lang !== 'en') {
            result = await this._fetchSummary(title, 'en');
            if (result) {
                if (userLat !== undefined && userLon !== undefined) {
                    const coords = await this._fetchCoordinates(title, 'en');
                    if (coords) {
                        const dist = WikipediaEnricher._haversineKm(userLat, userLon, coords.lat, coords.lon);
                        if (dist > 50)
                            result = null;
                        else {
                            result.lat = coords.lat;
                            result.lon = coords.lon;
                        }
                    }
                }
                if (result)
                    return result;
            }
        }
        // 3. City-scoped search: "Sandwirth Klagenfurt" disambiguates better than "Sandwirth" alone
        if (city) {
            const cityHit = await this.search(`${title} ${city}`, lang);
            if (cityHit) {
                result = await this._fetchSummary(cityHit, lang);
                if (result)
                    return result;
            }
        }
        // 4. Generic search with geo-validation
        const searchHit = await this.search(title, lang);
        if (searchHit && searchHit !== title) {
            let searchCoords = null;
            if (userLat !== undefined && userLon !== undefined) {
                searchCoords = await this._fetchCoordinates(searchHit, lang);
                if (searchCoords) {
                    const dist = WikipediaEnricher._haversineKm(userLat, userLon, searchCoords.lat, searchCoords.lon);
                    if (dist > 50) {
                        // Search result is far away — likely a different place with a similar name.
                        // Fall through to geosearch instead of returning null.
                        return this._geoFallback(title, lang, userLat, userLon);
                    }
                }
            }
            result = await this._fetchSummary(searchHit, lang);
            if (result) {
                if (searchCoords) {
                    result.lat = searchCoords.lat;
                    result.lon = searchCoords.lon;
                }
                return result;
            }
        }
        // 5. Geosearch: find Wikipedia articles near the user, fuzzy-match against title.
        // Mehrsprachig: erst die Guide-Sprache, dann die LANDESSPRACHE des Standorts
        // (im Ausland liegen die Denkmäler auf der lokalen Wiki), dann Englisch.
        // Fremdsprachige Treffer liefern das BILD (sprachneutral); ihr Extract wird
        // verworfen — ein kroatischer Beschreibungstext hilft der deutschen Card nicht.
        //
        // ZWEI Suchzentren: um den USER (nah) UND um die BOT-RAT-Position (die
        // erzählte Entität liegt oft km entfernt — Alex' Festung war 3,4km weg,
        // die 500m-User-Suche erreichte sie nie; die Suche um den Rat schon).
        const centers = [];
        if (userLat !== undefined && userLon !== undefined)
            centers.push({ lat: userLat, lon: userLon });
        if (guessLat !== undefined && guessLon !== undefined &&
            !(userLat !== undefined && userLon !== undefined &&
                WikipediaEnricher._haversineKm(userLat, userLon, guessLat, guessLon) < 0.3)) {
            centers.push({ lat: guessLat, lon: guessLon });
        }
        if (centers.length) {
            const langs = [lang];
            if (localLang && !langs.includes(localLang))
                langs.push(localLang);
            if (!langs.includes('en'))
                langs.push('en');
            for (const c of centers) {
                for (const L of langs) {
                    const hit = await this._geoFallback(title, L, c.lat, c.lon);
                    if (!hit)
                        continue;
                    if (L === lang)
                        return hit;
                    if (hit.image && hit.imageIsPhoto) {
                        hit.extract = '';
                        return hit;
                    }
                }
            }
        }
        return null;
    }
    /**
     * Last-resort: find Wikipedia articles near the user's position and
     * check if any of them match the topic title (fuzzy).
     */
    async _geoFallback(title, lang, lat, lon) {
        const api = `https://${lang}.wikipedia.org/w/api.php?action=query&format=json&origin=*&list=geosearch&gscoord=${lat}|${lon}&gsradius=500&gslimit=20`;
        try {
            const r = await fetch(api);
            if (!r.ok)
                return null;
            const d = await r.json();
            const results = d?.query?.geosearch || [];
            if (results.length === 0)
                return null;
            // Fuzzy match: does any nearby article title overlap with our topic?
            const titleLower = title.toLowerCase();
            const titleWords = titleLower.split(/[\s,\-()]+/).filter((w) => w.length > 3);
            const match = results.find((r) => {
                const rLower = r.title.toLowerCase();
                // Exact substring match
                if (rLower.includes(titleLower) || titleLower.includes(rLower))
                    return true;
                // Word overlap (at least one significant word matches)
                return titleWords.some((w) => rLower.includes(w));
            });
            if (match) {
                const s = await this._fetchSummary(match.title, lang);
                // Attach the geosearch coordinate — the REAL position of this article
                // near the user (drives the map marker, not the bot's guess).
                if (s && typeof match.lat === 'number' && typeof match.lon === 'number') {
                    s.lat = match.lat;
                    s.lon = match.lon;
                }
                return s;
            }
            // Kein Titel-Match — aber Namen divergieren oft komplett („Orahovica
            // Festung" vs. „Ružica grad", derselbe Bau). Der NÄCHSTE Artikel in
            // unmittelbarer Nähe (≤300 m, geosearch sortiert nach Distanz) ist beim
            // Erzählen über den Standort fast immer das Motiv → als BILD- UND
            // POSITIONS-Quelle nutzen (nur mit echtem Foto; Extract verwerfen, der
            // Text der Card bleibt die Narration selbst).
            const nearest = results[0];
            if (nearest && typeof nearest.dist === 'number' && nearest.dist <= 300) {
                const s = await this._fetchSummary(nearest.title, lang);
                if (s?.image && s.imageIsPhoto) {
                    s.extract = '';
                    if (typeof nearest.lat === 'number' && typeof nearest.lon === 'number') {
                        s.lat = nearest.lat;
                        s.lon = nearest.lon;
                    }
                    return s;
                }
            }
        }
        catch { /* ignore */ }
        return null;
    }
    async search(query, lang = 'de') {
        const api = `https://${lang}.wikipedia.org/w/api.php?action=query&format=json&origin=*&list=search&srsearch=${encodeURIComponent(query)}&srlimit=1&srprop=`;
        try {
            const r = await fetch(api);
            if (!r.ok)
                return null;
            const d = await r.json();
            const hit = d?.query?.search?.[0]?.title;
            return typeof hit === 'string' ? hit : null;
        }
        catch {
            return null;
        }
    }
    async _fetchSummary(title, lang) {
        const api = `https://${lang}.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title.replace(/ /g, '_'))}`;
        try {
            const r = await fetch(api);
            if (!r.ok)
                return null;
            const d = await r.json();
            const rawImage = d?.originalimage?.source || d?.thumbnail?.source || null;
            // Check image quality — is it a photo or a map/diagram?
            const imageIsPhoto = rawImage ? WikipediaEnricher._isPhoto(rawImage, d) : false;
            const image = imageIsPhoto ? rawImage : null;
            const extract = d?.extract || '';
            const url = d?.content_urls?.desktop?.page ||
                `https://${lang}.wikipedia.org/wiki/${encodeURIComponent((d?.title || title).replace(/ /g, '_'))}`;
            const imgW = d?.originalimage?.width || d?.thumbnail?.width || 0;
            const imgH = d?.originalimage?.height || d?.thumbnail?.height || 0;
            const imageSize = !image ? 'none'
                : imgW >= 800 ? 'hero'
                    : imgW >= 400 ? 'thumbnail'
                        : 'icon';
            return { title: d?.title || title, extract, image, imageIsPhoto, imageWidth: imgW, imageHeight: imgH, imageSize, url, lang };
        }
        catch {
            return null;
        }
    }
    /**
     * Heuristic: is this Wikipedia image a photo/portrait/artwork
     * (worth showing) or a map/diagram/coat of arms (skip)?
     */
    /** Public static check — can be used to filter bot-provided image URLs */
    static isPhotoUrl(imageUrl) {
        const decoded = decodeURIComponent(imageUrl);
        if (BAD_SVG.test(decoded))
            return false;
        if (BAD_IMAGE_PATTERNS.test(decoded))
            return false;
        return true;
    }
    /** Fetch article coordinates via Wikipedia API (null if no coords) */
    async _fetchCoordinates(title, lang) {
        const api = `https://${lang}.wikipedia.org/w/api.php?action=query&format=json&origin=*&prop=coordinates&titles=${encodeURIComponent(title)}`;
        try {
            const r = await fetch(api);
            if (!r.ok)
                return null;
            const d = await r.json();
            const pages = d?.query?.pages || {};
            for (const page of Object.values(pages)) {
                const coords = page?.coordinates?.[0];
                if (coords && typeof coords.lat === 'number') {
                    return { lat: coords.lat, lon: coords.lon };
                }
            }
            return null;
        }
        catch {
            return null;
        }
    }
    static _haversineKm(lat1, lon1, lat2, lon2) {
        const R = 6371;
        const toRad = (d) => (d * Math.PI) / 180;
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
    static _isPhoto(imageUrl, apiResponse) {
        const decoded = decodeURIComponent(imageUrl);
        // SVG source
        if (BAD_SVG.test(decoded))
            return false;
        // Filename patterns (maps, diagrams, etc.)
        if (BAD_IMAGE_PATTERNS.test(decoded))
            return false;
        // Wikipedia /de/ uploads (not Commons) are almost always maps/diagrams/logos.
        // Real photos live on Commons (/wikipedia/commons/).
        // Example: /wikipedia/de/2/25/Klagenfurt.jpg → Bezirkskarte, not a photo.
        if (/\/wikipedia\/(?:de|en|fr|it|es|sl)\//.test(decoded) && !/\/wikipedia\/commons\//.test(decoded)) {
            return false;
        }
        // Geographic entity detection via Wikipedia description field.
        const desc = (apiResponse?.description || '').toLowerCase();
        if (desc && _isGeographicDescription(desc))
            return false;
        // Check thumbnail dimensions — tiny images are icons, not photos
        const thumbWidth = apiResponse?.thumbnail?.width;
        const thumbHeight = apiResponse?.thumbnail?.height;
        if (thumbWidth && thumbHeight) {
            if (thumbWidth < MIN_USEFUL_DIMENSION && thumbHeight < MIN_USEFUL_DIMENSION) {
                return false;
            }
        }
        // Passed all checks — likely a real photo
        return true;
    }
}
//# sourceMappingURL=WikipediaEnricher.js.map